/* eslint-disable no-useless-escape */
import {
  CONTRATO_STATUS_APROVADO,
  CONTRATO_STATUS_EM_EDICAO,
  CONTRATO_STATUS_NEGADO,
} from 'library/ContratoStatus';
import {
  CONTRATO_ETAPAS_PARAMS_URL,
  ContratoStatus,
  STEP_DADOS_CONTRATADO,
  STEP_DADOS_COOPERATIVA,
  STEP_DOCUMENTOS,
  URL_ENTE_DESCENTRALIZADO,
  VersaoContratoEnum,
} from 'models/contratos';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  emailIsValid,
  requiredIsValid,
  strCidadeRequiredIsValid,
} from 'utils/ValidationUtils';
import { CreateRouteParams, EditRouteParams } from '../__types';
import { useLocation, useParams } from 'react-router-dom';
import { z } from 'zod';
import moment from 'moment';

const useValidations = () => {
  const [loading] = useState(false);
  const [editing] = useState(false);
  const auth = useSelector((state: RootState) => state.auth);
  const contratosReducer = useSelector(
    (state: RootState) => state.allContratos
  );
  const contratosAssessoresReducer = useSelector(
    (state: RootState) => state.contratosDescentralizadosAssessores
  );
  const contratosEntePublicoReducer = useSelector(
    (state: RootState) => state.contratosDescentralizadosEntePublico
  );
  const contratosEntePrivadoReducer = useSelector(
    (state: RootState) => state.contratosDescentralizadosEntePrivado
  );
  const currentStepContrato = useSelector(
    (state: RootState) => state.allContratos.currentContratoStep
  );
  const languageValues = useSelector(
    (state: RootState) => state.languages.languageValues
  );
  const params = useParams<CreateRouteParams & EditRouteParams>();
  const location = useLocation();

  const estadoSchema = useMemo(() => z.number().min(1, { message: languageValues.validations.estado.required }), [languageValues.validations.estado.required]);

  const cidadeSchema = useMemo(() => z.string().min(1, { message: languageValues.validations.cidade.required }), [languageValues.validations.cidade.required]);

  const escolaSchema = useMemo(() => z.object({
    escola: z.string()
      .min(1, { message: languageValues.validations.escola.required })
      .refine(value => value.trim().length > 0, { message: languageValues.validations.escola.required }),
  }), [languageValues.validations.escola.required]);

  const representanteSchema = useMemo(() => z.object({
    representante: z.string()
      .min(1, { message: languageValues.validations.representante.required })
      .refine(value => value.trim().length > 0, { message: languageValues.validations.representante.required }),
  }), [languageValues.validations.representante.required]);

  const dateSchema = useMemo(() => z.string().refine((date) => moment(date, 'DD/MM/YYYY', true).isValid(), {
    message: languageValues.validations.data.required,
  }), [languageValues.validations.data.required]);

  const emailCoopValueIsValid = useCallback(() => {
    const assesorValue = contratosAssessoresReducer.contratoEditing.emailCoop;
    const entePublicoValue = contratosEntePublicoReducer.contratoEditing.emailCoop;
    const EntePrivadoValue = contratosEntePrivadoReducer.contratoEditing.emailCoop;

    const required = requiredIsValid(
      assesorValue || entePublicoValue || EntePrivadoValue,
      languageValues.validations.email.required
    );
    const validation = emailIsValid(
      assesorValue || entePublicoValue || EntePrivadoValue,
      languageValues.validations.email.validation
    );
    if (!required.status) return required;
    if (!validation.status) return validation;
    return { status: true };
  }, [contratosAssessoresReducer.contratoEditing.emailCoop,
  contratosEntePrivadoReducer.contratoEditing.emailCoop,
  contratosEntePublicoReducer.contratoEditing.emailCoop,
  languageValues.validations.email.required,
  languageValues.validations.email.validation]);

  const codCoopValueIsValid = useCallback(() => {
    const { codCoop: codCoopAssessores } = contratosAssessoresReducer.contratoEditing;
    const { codCoop: codCoopEntePublico } = contratosEntePublicoReducer.contratoEditing;
    const { codCoop: codCoopEntePrivado } = contratosEntePrivadoReducer.contratoEditing;

    if (codCoopAssessores.length > 0 || codCoopEntePublico.length > 0 || codCoopEntePrivado.length > 0) {
      return { status: true };
    } else {
      return {
        status: false,
        error: languageValues.validations.cooperativa.required,
      };
    }
  }, [contratosAssessoresReducer.contratoEditing,
  contratosEntePrivadoReducer.contratoEditing,
  contratosEntePublicoReducer.contratoEditing,
  languageValues.validations.cooperativa.required]);

  const isEmpresaFieldNull = () => {
    const idEmpresa = contratosAssessoresReducer.contratoEditing.idEmpresa;
    return !idEmpresa;
  };

  const idEmpresaIsValid = () => {
    if (contratosAssessoresReducer.contratoEditing.idEmpresa > 0) {
      return { status: true };
    } else {
      return {
        status: false,
        error: languageValues.validations.empresaAssessor.required,
      };
    }
  };

  const cpfAssessorIsValid = () => {
    if (isEmpresaFieldNull()) {
      return { status: true };
    }
    const cpfAssessores =
      contratosAssessoresReducer.contratoEditing.cpfAssessores;
    if (cpfAssessores && cpfAssessores.length > 0) {
      return { status: true };
    }
    return {
      status: false,
      error: languageValues.validations.assessor.required,
    };
  };

  const valorAssessoriaIsValid = () => {
    const value = contratosAssessoresReducer.contratoEditing.valorAssessoria;
    if (value === null || value === undefined) {
      return {
        status: false,
        error: languageValues.cadastroContratoRequiredAssessoria,
      };
    }
    return { status: true };
  };

  const valorReuniaoIsValid = () => {
    const value = contratosAssessoresReducer.contratoEditing.valorReuniao;
    if (value === null || value === undefined) {
      return {
        status: false,
        error: languageValues.cadastroContratoRequiredReuniao,
      };
    }
    return { status: true };
  };

  const valorFormacaoIsValid = () => {
    const value = contratosAssessoresReducer.contratoEditing.valorFormacao;
    if (value === null || value === undefined) {
      return {
        status: false,
        error: languageValues.cadastroContratoRequiredFormacao,
      };
    }
    return { status: true };
  };

  const valueProgramaEducacionaisIsValid = () => {
    if (
      contratosAssessoresReducer.contratoEditing.programasEducacionais.length > 0 ||
      contratosEntePublicoReducer.contratoEditing.programasEducacionais.length > 0 ||
      contratosEntePrivadoReducer.contratoEditing.programasEducacionais.length > 0
    ) {
      return { status: true };
    } else {
      return {
        status: false,
        error: languageValues.cadastroContratoRequiredProgramasEducacionais,
      };
    }
  };

  const validateDateField = <T extends { [key: string]: string }>(
    date: string,
    comparaData: string,
    validateFunction: (date: string, comparaData: string, errorMessages: T) => string,
    errorMessages: T
  ) => {
    const error = validateFunction(date, comparaData, errorMessages);
    return { status: !error, error };
  };

  const validateDataInicio = useCallback((dataInicio: string, dataFim: string, errorMessages: { after: string, same: string }) => {
    const result = dateSchema.safeParse(dataInicio);
    if (!result.success) {
      return result.error.errors[0].message;
    }
    const dataInicioMoment = moment(dataInicio, 'DD/MM/YYYY');
    const dataFimMoment = moment(dataFim, 'DD/MM/YYYY')
    if (dataInicioMoment.isAfter(dataFimMoment)) {
      return errorMessages.after;
    } else if (dataInicioMoment.isSame(dataFimMoment)) {
      return errorMessages.same;
    }

    return '';
  }, [dateSchema]);

  const validateDataFim = useCallback((dataFim: string, dataInicio: string, errorMessages: { before: string, same: string }) => {
    const result = dateSchema.safeParse(dataFim);
    if (!result.success) {
      return result.error.errors[0].message;
    }
    const dataFimMoment = moment(dataFim, 'DD/MM/YYYY');
    const dataInicioMoment = moment(dataInicio, 'DD/MM/YYYY');
    if (dataFimMoment.isBefore(dataInicioMoment)) {
      return errorMessages.before;
    } else if (dataFimMoment.isSame(dataInicioMoment)) {
      return errorMessages.same;
    }
    return '';
  }, [dateSchema]);

  const dataInicioIsValid = useCallback((dataInicio: string, dataFim: string, errorMessages: { after: string, same: string }) => {
    return validateDateField(dataInicio, dataFim, validateDataInicio, errorMessages);
  }, [validateDataInicio]);

  const dataFimIsValid = useCallback((dataFim: string, dataInicio: string, errorMessages: { before: string, same: string }) => {
    return validateDateField(dataFim, dataInicio, validateDataFim, errorMessages);
  }, [validateDataFim]);

  const editingF = () => {
    return editing || false;
  };

  const hasPermissions = () => {
    if (!contratosAssessoresReducer.contratoEditing.ativo) {
      return false;
    }
    if (editingF()) {
      return (
        [
          CONTRATO_STATUS_EM_EDICAO,
          CONTRATO_STATUS_APROVADO,
          CONTRATO_STATUS_NEGADO,
        ].indexOf(contratosAssessoresReducer.contratoEditing.flgStatus) > -1
      );
    }
    return (
      contratosAssessoresReducer.contratoEditing.flgStatus <=
      CONTRATO_STATUS_EM_EDICAO &&
      auth.userPermissions.contratos.assessor.register
    );
  };

  const fieldDisabled = () => {
    if (!hasPermissions()) {
      return true;
    }
    const isEditing = editingF();
    if (isEditing && !auth.userPermissions.contratos.assessor.edit) {
      return true;
    }
    return loading;
  };

  const nomCidadeValueIsValid = () => {
    const required = strCidadeRequiredIsValid(
      contratosEntePublicoReducer.contratoEditing.nomCidade,
      languageValues.validations.cidade.required
    );
    if (!required.status) {
      return required;
    }
    return {
      status: true,
    };
  };

  const validaValorEstado = (value: number) => {
    const result = estadoSchema.safeParse(value);
    if (!result.success) {
      return {
        status: false,
        error: result.error.errors[0].message,
      };
    }
    return {
      status: true,
    };
  };

  const validaValorCidade = (value: string) => {
    const result = cidadeSchema.safeParse(value);
    if (!result.success) {
      return {
        status: false,
        error: result.error.errors[0].message,
      };
    }
    return {
      status: true,
    };
  };

  const validaValorCampoEscola = (value: string, isFieldDisabled?: boolean) => {
    if (isFieldDisabled) {
      return { status: true, error: '' };
    }
    try {
      const stringValue = String(value);
      escolaSchema.parse({ escola: stringValue });
      return { status: true, error: '' };
    } catch (e: any) {
      return { status: false, error: e.errors[0].message };
    }
  };

  const validaValorCampoRepresentante = (value: string, isFieldDisabled?: boolean) => {
    if (isFieldDisabled) {
      return { status: true, error: '' };
    }
    try {
      const stringValue = String(value);
      representanteSchema.parse({ representante: stringValue });
      return { status: true, error: '' };
    } catch (e: any) {
      return { status: false, error: e.errors[0].message };
    }
  };

  const desabilitaCampoCidade = (idEstado: boolean) => {
    return idEstado
      ? true
      : false;
  };

  const validaValorNomeEstado = (idEstado: number) => {
    return validaValorEstado(idEstado);
  };

  const validaValorNomeCidade = (nomeCidade: string) => {
    return validaValorCidade(nomeCidade);
  };

  const ValidGenericFormCooperaiva = () => {
    return codCoopValueIsValid().status && emailCoopValueIsValid().status;
  };

  const validDadosAssessorDescentralizado = () => {
    const dataInicioValidationResult = dataInicioIsValid(
      contratosAssessoresReducer.contratoEditing.dataInicio,
      contratosAssessoresReducer.contratoEditing.dataFim,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );

    const dataFimValidationResult = dataFimIsValid(
      contratosAssessoresReducer.contratoEditing.dataFim,
      contratosAssessoresReducer.contratoEditing.dataInicio,
      {
        before: languageValues.dataFimError,
        same: languageValues.dataFimIgualDataInicioError,
      }
    );
    return (
      contratosAssessoresReducer.contratoEditing.flgAnaliseJuridica === true &&
      contratosAssessoresReducer.contratoEditing.valorAssessoria >= 0 &&
      contratosAssessoresReducer.contratoEditing.valorFormacao >= 0 &&
      contratosAssessoresReducer.contratoEditing.valorReuniao >= 0 &&
      contratosAssessoresReducer.contratoEditing.idEmpresa > 0 &&
      contratosAssessoresReducer.contratoEditing.cpfAssessores.length > 0 &&
      contratosAssessoresReducer.contratoEditing.programasEducacionais.length > 0 &&
      dataInicioValidationResult.status &&
      dataFimValidationResult.status
    );
  };

  const validDadosAssessorAditivo = () => {
    const dataInicioValidationResult = dataInicioIsValid(
      contratosAssessoresReducer.contratoEditing.dataInicio,
      contratosAssessoresReducer.contratoEditing.dataFim,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );

    const dataFimValidationResult = dataFimIsValid(
      contratosAssessoresReducer.contratoEditing.dataFim,
      contratosAssessoresReducer.contratoEditing.dataInicio,
      {
        before: languageValues.dataFimError,
        same: languageValues.dataFimIgualDataInicioError,
      }
    );
    return (
      contratosAssessoresReducer.contratoEditing.idEmpresa > 0 &&
      contratosAssessoresReducer.contratoEditing.cpfAssessores.length > 0 &&
      contratosAssessoresReducer.contratoEditing.valorFormacao >= 0 &&
      contratosAssessoresReducer.contratoEditing.valorAssessoria >= 0 &&
      contratosAssessoresReducer.contratoEditing.valorReuniao >= 0 &&
      contratosAssessoresReducer.contratoEditing.flgAnaliseJuridica === true &&
      dataInicioValidationResult.status &&
      dataFimValidationResult.status
    );
  };

  const validDadosAssessorDistrato = () => {
    const dataDistrato =
      contratosAssessoresReducer.contratoEditing.dataDistrato;
    const dateValidation = validateDateDistrato(dataDistrato);

    if (!dateValidation.status) {
      return false;
    }
    return (
      contratosAssessoresReducer.contratoEditing.idEmpresa > 0 &&
      contratosAssessoresReducer.contratoEditing.cpfAssessores.length > 0 &&
      contratosAssessoresReducer.contratoEditing.flgAnaliseJuridica === true
    );
  };

  const validDadosEntePublicoDescentralizado = () => {
    const dataInicioValidationResult = dataInicioIsValid(
      contratosEntePublicoReducer.contratoEditing.dataInicio,
      contratosEntePublicoReducer.contratoEditing.dataFim,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );

    const dataFimValidationResult = dataFimIsValid(
      contratosEntePublicoReducer.contratoEditing.dataFim,
      contratosEntePublicoReducer.contratoEditing.dataInicio,
      {
        before: languageValues.dataFimError,
        same: languageValues.dataFimIgualDataInicioError,
      }
    );
    return (
      contratosEntePublicoReducer.contratoEditing.idEstado > 0 &&
      !nomCidadeValueIsValid().status === false &&
      dataInicioValidationResult.status &&
      dataFimValidationResult.status &&
      contratosEntePublicoReducer.contratoEditing.programasEducacionais.length > 0 &&
      contratosEntePublicoReducer.contratoEditing.flgAnaliseJuridica === true
    );
  };

  const validDadosEntePublicoAditivo = () => {
    const dataInicioValidationResult = dataInicioIsValid(
      contratosEntePublicoReducer.contratoEditing.dataInicio,
      contratosEntePublicoReducer.contratoEditing.dataFim,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );

    const dataFimValidationResult = dataFimIsValid(
      contratosEntePublicoReducer.contratoEditing.dataFim,
      contratosEntePublicoReducer.contratoEditing.dataInicio,
      {
        before: languageValues.dataFimError,
        same: languageValues.dataFimIgualDataInicioError,
      }
    );
    return (
      dataInicioValidationResult.status &&
      dataFimValidationResult.status &&
      contratosEntePublicoReducer.contratoEditing.programasEducacionais.length > 0 &&
      contratosEntePublicoReducer.contratoEditing.flgAnaliseJuridica === true
    );
  };

  const validDadosEntePublicoDistrato = () => {
    const dataDistrato =
      contratosEntePublicoReducer.contratoEditing.dataDistrato;
    const dateValidation = validateDateDistrato(dataDistrato);

    if (!dateValidation.status) {
      return false;
    }
    return (
      contratosEntePublicoReducer.contratoEditing.idEstado > 0 &&
      !nomCidadeValueIsValid().status === false &&
      contratosEntePublicoReducer.contratoEditing.flgAnaliseJuridica === true
    );
  };

  const validDadosEntePrivadoDescentralizado = () => {
    const dataInicioValidationResult = dataInicioIsValid(
      contratosEntePrivadoReducer.contratoEditing.dataInicio,
      contratosEntePrivadoReducer.contratoEditing.dataFim,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );
    const dataFimValidationResult = dataFimIsValid(
      contratosEntePrivadoReducer.contratoEditing.dataFim,
      contratosEntePrivadoReducer.contratoEditing.dataInicio,
      {
        before: languageValues.dataFimError,
        same: languageValues.dataFimIgualDataInicioError,
      }
    );
    return (
      validaValorCampoEscola(contratosEntePrivadoReducer.contratoEditing.nomEscola).status &&
      validaValorCampoRepresentante(contratosEntePrivadoReducer.contratoEditing.cpfProfessor).status &&
      dataInicioValidationResult.status &&
      dataFimValidationResult.status &&
      contratosEntePrivadoReducer.contratoEditing.programasEducacionais.length > 0 &&
      contratosEntePrivadoReducer.contratoEditing.flgAnaliseJuridica === true
    )
  };

  const validDadosEntePrivadoAditivo = () => {
    const dataInicioValidationResult = dataInicioIsValid(
      contratosEntePrivadoReducer.contratoEditing.dataInicio,
      contratosEntePrivadoReducer.contratoEditing.dataFim,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );

    const dataFimValidationResult = dataFimIsValid(
      contratosEntePrivadoReducer.contratoEditing.dataFim,
      contratosEntePrivadoReducer.contratoEditing.dataInicio,
      {
        before: languageValues.dataFimError,
        same: languageValues.dataFimIgualDataInicioError,
      }
    );

    return (
      validaValorCampoEscola(contratosEntePrivadoReducer.contratoEditing.nomEscola).status &&
      validaValorCampoRepresentante(contratosEntePrivadoReducer.contratoEditing.cpfProfessor).status &&
      dataInicioValidationResult.status &&
      dataFimValidationResult.status &&
      contratosEntePrivadoReducer.contratoEditing.flgAnaliseJuridica === true
    );
  };

  const validDadosEntePrivadoDistrato = () => {
    const dataDistrato = contratosEntePrivadoReducer.contratoEditing.dataDistrato;
    const dateValidation = validateDateDistrato(dataDistrato);
    if (!dateValidation.status) {
      return false;
    }
    return (
      validaValorCampoEscola(contratosEntePrivadoReducer.contratoEditing.nomEscola).status &&
      validaValorCampoRepresentante(contratosEntePrivadoReducer.contratoEditing.cpfProfessor).status &&
      contratosEntePrivadoReducer.contratoEditing.flgAnaliseJuridica === true
    )
  };

  const validateDateDistrato = (data: string | undefined) => {
    if (!data) {
      return { status: false, error: languageValues.dataInvalida };
    }
    const regex = /^\d{2}[\/-]\d{2}[\/-]\d{4}$/;
    if (!data.match(regex)) {
      return { status: false, error: languageValues.dataInvalida };
    }
    const [day, month, year] = data.split(/[-\/]/).map(Number);
    if (month < 1 || month > 12) {
      return { status: false, error: languageValues.mesInvalido };
    }
    if (day < 1 || day > 31) {
      return { status: false, error: languageValues.diaInvalido };
    }
    if (!/^(19|20)/.test(String(year))) {
      return { status: false, error: languageValues.anoInvalido };
    }

    return { status: true, error: '' };
  };

  const habilitaBotaoAvancaStep = () => {
    if (currentStepContrato === STEP_DADOS_COOPERATIVA) {
      return !ValidGenericFormCooperaiva();
    }
    if (currentStepContrato === STEP_DADOS_CONTRATADO) {
      return (
        (location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ASSESSOR) && !validDadosAssessorDescentralizado()) ||
        (location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ENTE_PUBLICO) && !validDadosEntePublicoDescentralizado()) ||
        (location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ENTE_PRIVADO) && !validDadosEntePrivadoDescentralizado())
      );
    }
    if (currentStepContrato === STEP_DOCUMENTOS) {
      return false;
    }
    return true;
  };

  const validaStatusContratoFluxo = () => {
    const flgStatusContratoAssessor = contratosAssessoresReducer.contratoEditing.flgStatus;
    const flgStatusContratoEntePublico = contratosEntePublicoReducer.contratoEditing.flgStatus;
    const flgStatusContratoEntePrivado = contratosEntePrivadoReducer.contratoEditing.flgStatus;

    const isButtonDisabledAssessor =
      flgStatusContratoAssessor === ContratoStatus.VIGENTE ||
      flgStatusContratoAssessor === ContratoStatus.ENCERRADO ||
      flgStatusContratoAssessor === ContratoStatus.DESATIVADO ||
      flgStatusContratoAssessor === ContratoStatus.NAO_INICIADO;

    const isButtonDisabledEntePublico =
      flgStatusContratoEntePublico === ContratoStatus.VIGENTE ||
      flgStatusContratoEntePublico === ContratoStatus.ENCERRADO ||
      flgStatusContratoEntePublico === ContratoStatus.DESATIVADO ||
      flgStatusContratoEntePublico === ContratoStatus.NAO_INICIADO;

    const isButtonDisabledEntePrivado =
      flgStatusContratoEntePrivado === ContratoStatus.VIGENTE ||
      flgStatusContratoEntePrivado === ContratoStatus.ENCERRADO ||
      flgStatusContratoEntePrivado === ContratoStatus.DESATIVADO ||
      flgStatusContratoEntePrivado === ContratoStatus.NAO_INICIADO;

    return !(isButtonDisabledAssessor || isButtonDisabledEntePublico || isButtonDisabledEntePrivado);
  }

  const isContratoAtivoAssessor = () => {
    if (params.idContrato === CONTRATO_ETAPAS_PARAMS_URL.CADASTRO) {
      return true;
    } else if (contratosAssessoresReducer.contratoEditing.ativo === false) {
      return false;
    }
    return contratosAssessoresReducer.contratoEditing.ativo === true;
  };

  const isContratoAtivoEntePublico = () => {
    switch (true) {
      case params.idContrato === CONTRATO_ETAPAS_PARAMS_URL.CADASTRO:
        return true;
      case contratosEntePublicoReducer.contratoEditing.flgAtivo === true &&
        contratosEntePublicoReducer.contratoEditing.flgStatus === ContratoStatus.EM_EDICAO:
        return true;
      case contratosEntePublicoReducer.contratoEditing.flgAtivo === false:
        return false;
      default:
        return false;
    }
  };

  const isContratoAtivoEntePrivado = () => {
    switch (true) {
      case params.idContrato === CONTRATO_ETAPAS_PARAMS_URL.CADASTRO:
        return true;
      case contratosEntePrivadoReducer.contratoEditing.flgAtivo === true &&
        contratosEntePrivadoReducer.contratoEditing.flgStatus === ContratoStatus.EM_EDICAO:
        return true;
      case contratosEntePrivadoReducer.contratoEditing.flgAtivo === false:
        return false;
      default:
        return false;
    }
  };

  const desabilitaCampoEspecificoAditivoEDistrato = () => {
    const versaoContrato =
      contratosReducer.adendoEditing.versaoContrato;
    if (versaoContrato === VersaoContratoEnum.CONTRATO) {
      return false;
    }
    return true;
  };

  return {
    validateDateDistrato,
    emailCoopValueIsValid,
    validDadosEntePublicoDistrato,
    codCoopValueIsValid,
    isContratoAtivoEntePublico,
    isContratoAtivoAssessor,
    isContratoAtivoEntePrivado,
    validDadosAssessorAditivo,
    validDadosAssessorDistrato,
    validaValorCampoEscola,
    validaValorCampoRepresentante,
    validDadosEntePrivadoAditivo,
    validDadosEntePrivadoDistrato,
    desabilitaCampoEspecificoAditivoEDistrato,
    validDadosEntePrivadoDescentralizado,
    validaStatusContratoFluxo,
    valorFormacaoIsValid,
    idEmpresaIsValid,
    isEmpresaFieldNull,
    habilitaBotaoAvancaStep,
    hasPermissions,
    valorReuniaoIsValid,
    valorAssessoriaIsValid,
    validaValorNomeEstado,
    validaValorNomeCidade,
    desabilitaCampoCidade,
    validDadosAssessorDescentralizado,
    validDadosEntePublicoDescentralizado,
    validDadosEntePublicoAditivo,
    ValidGenericFormCooperaiva,
    validateDataInicio,
    validateDataFim,
    dataInicioIsValid,
    cpfAssessorIsValid,
    valueProgramaEducacionaisIsValid,
    dataFimIsValid,
    fieldDisabled,
    editing,
  };
};

export default useValidations;
